import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { toast } from 'sonner';

// Smart backend URL detection that works in preview and deployment
const getBackendURL = () => {
  // First try environment variable
  if (process.env.REACT_APP_API_URL) {
    return process.env.REACT_APP_API_URL;
  }

  // Fallback for different environments
  const origin = window.location.origin;

  // If we're in localhost (development), use localhost backend
  if (origin.includes('localhost') || origin.includes('127.0.0.1')) {
    return 'http://localhost:8001';
  }

  // For deployed environments, use the same origin (report generator should be proxied)
  // Or use a specific subdomain/port if configured
  return origin;
};

const API_BASE_URL = getBackendURL();

function AdminLogin() {
  const [password, setPassword] = useState('');
  const [loading, setLoading] = useState(false);
  const navigate = useNavigate();

  const handleSubmit = async (e) => {
    e.preventDefault();

    if (!password) {
      toast.error('Please enter a password');
      return;
    }

    setLoading(true);

    try {
      const response = await fetch(`${API_BASE_URL}/api/admin/auth/login`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({ password }),
      });

      if (response.ok) {
        const data = await response.json();

        // Store token in localStorage
        localStorage.setItem('admin_token', data.token);

        toast.success('Login successful');
        navigate('/admin/risk-report');
      } else {
        const error = await response.json();
        toast.error(error.detail || 'Invalid password');
      }
    } catch (error) {
      console.error('Login error:', error);
      if (error.name === 'AbortError') {
        toast.error('Request timed out. Please check if the backend server is running.');
      } else if (
        error.message.includes('Failed to fetch') ||
        error.message.includes('NetworkError')
      ) {
        toast.error(
          'Cannot connect to server. Please check if the backend is running on ' + API_BASE_URL
        );
      } else {
        toast.error('Failed to connect to server: ' + error.message);
      }
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen flex items-center justify-center p-4" style={{ background: 'var(--page-bg)' }}>
      <div className="rounded-xl border border-border p-8 w-full max-w-md" style={{ background: 'var(--surface)' }}>
        <div className="text-center mb-8">
          <h1 className="text-3xl font-bold text-text-primary mb-2">Admin Login</h1>
          <p className="text-text-muted">Risk Assessment Report Generator</p>
        </div>

        <form onSubmit={handleSubmit} className="space-y-6">
          <div>
            <label htmlFor="password" className="block text-sm font-medium text-text-primary mb-2">
              Password
            </label>
            <input
              type="password"
              id="password"
              value={password}
              onChange={(e) => setPassword(e.target.value)}
              className="w-full px-4 py-2 border border-input rounded-lg bg-background text-foreground focus:outline-none focus:ring-2 focus:ring-primary"
              placeholder="Enter admin password"
              disabled={loading}
              autoFocus
            />
          </div>

          <button
            type="submit"
            disabled={loading}
            className="w-full bg-primary text-primary-foreground py-3 rounded-lg font-semibold hover:opacity-90 transition-all duration-200 disabled:opacity-50 disabled:cursor-not-allowed"
          >
            {loading ? 'Logging in...' : 'Login'}
          </button>
        </form>

        <div className="mt-6 text-center text-sm text-text-muted">
          <p>Wahle-Tec Admin Access</p>
        </div>
      </div>
    </div>
  );
}

export default AdminLogin;
